import { useState, useCallback } from 'react';
import { message } from 'antd';
import { InspectionItemStatus } from '@/shared/enums/InspectionStatus';
import aiAnalysisService, { AutomaticReportResponse } from '@/services/aiAnalysis.service';
import { inspectionService, ScrapingJobStatus } from '@/services/inspection.service';

interface UseAutomaticLaudoProps {
  itemId: string | undefined;
  itemStatus: InspectionItemStatus | undefined;
  isAutomaticReport: boolean | undefined;
  onRefresh?: () => void;
}

interface UseAutomaticLaudoReturn {
  automaticReportData: AutomaticReportResponse | null;
  isAutomaticAiReport: boolean;
  isAutomaticAiProcessing: boolean;
  isAutomaticAiCompleted: boolean;
  isAutomaticAiFailed: boolean;
  isInAutomaticFlow: boolean;

  handleAutomaticReportReady: (reportData: AutomaticReportResponse) => void;
  handleAutomaticReportRetry: () => Promise<void>;
}
export function useAutomaticLaudo({
  itemId,
  itemStatus,
  isAutomaticReport,
  onRefresh,
}: UseAutomaticLaudoProps): UseAutomaticLaudoReturn {
  const [automaticReportData, setAutomaticReportData] = useState<AutomaticReportResponse | null>(null);

  const isAutomaticAiReport = isAutomaticReport === true;

  const isAutomaticAiProcessing = isAutomaticAiReport && (
    itemStatus === InspectionItemStatus.ProcessandoScraping ||
    itemStatus === InspectionItemStatus.AguardandoAnaliseIA ||
    itemStatus === InspectionItemStatus.ProcessandoAnaliseIA
  );

  const isAutomaticAiCompleted = isAutomaticAiReport &&
    itemStatus === InspectionItemStatus.LaudoAutomaticoGerado;

  const isAutomaticAiFailed = isAutomaticAiReport && (
    itemStatus === InspectionItemStatus.ScrapingFalhou ||
    itemStatus === InspectionItemStatus.AnaliseIAFalhou
  );

  const isInAutomaticFlow = isAutomaticAiReport &&
    (isAutomaticAiProcessing || isAutomaticAiFailed || isAutomaticAiCompleted);

  const handleAutomaticReportReady = useCallback((reportData: AutomaticReportResponse) => {
    setAutomaticReportData(reportData);
  }, []);

  const handleAutomaticReportRetry = useCallback(async () => {
    if (!itemId) return;

    try {
      message.loading('Reprocessando análise...', 0);

      const scrapingJob = await inspectionService.getScrapingJobByItemId(itemId);

      if (!scrapingJob || scrapingJob.status !== ScrapingJobStatus.Completed) {
        message.destroy();
        message.error('Scraping não concluído. Aguarde o processamento.');
        return;
      }

      await aiAnalysisService.triggerAiAnalysis(scrapingJob.id);

      message.destroy();
      message.success('Análise reiniciada com sucesso');
      onRefresh?.();
    } catch {
      message.destroy();
      message.error('Erro ao reprocessar análise');
    }
  }, [itemId, onRefresh]);

  return {
    automaticReportData,
    isAutomaticAiReport,
    isAutomaticAiProcessing,
    isAutomaticAiCompleted,
    isAutomaticAiFailed,
    isInAutomaticFlow,
    handleAutomaticReportReady,
    handleAutomaticReportRetry,
  };
}
